import React, { useState, useEffect } from "react";
import { getProfile, updateProfile } from "../api/profileClient";
import { useAuth } from "../contexts/AuthContext";

export default function ProfilePage() {
    const { checkAuth } = useAuth();
    const [profile, setProfile] = useState(null);
    const [displayName, setDisplayName] = useState("");
    const [isEditing, setIsEditing] = useState(false);
    const [status, setStatus] = useState("loading");
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        setStatus("loading");
        setError("");
        try {
            const data = await getProfile();
            setProfile(data);
            setDisplayName(data.displayName || "");
            setStatus("idle");
        } catch (err) {
            setStatus("error");
            setError(err.response?.data?.message || "Failed to load profile.");
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setStatus("saving");
        setError("");
        setSuccess("");
        try {
            const updated = await updateProfile({ displayName });
            setProfile(updated);
            setDisplayName(updated.displayName || "");
            setIsEditing(false);
            setStatus("idle");
            setSuccess("Profile updated successfully.");
            checkAuth();
        } catch (err) {
            setStatus("idle");
            setError(err.response?.data?.message || "Failed to update profile.");
        }
    };

    const handleCancel = () => {
        setDisplayName(profile?.displayName || "");
        setIsEditing(false);
        setError("");
        setSuccess("");
    };

    if (status === "loading") {
        return (
            <div className="max-w-lg mx-auto">
                <div className="card p-6">
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin w-6 h-6 border-2 border-gray-300 border-t-gray-900 rounded-full" />
                        <span className="ml-3 text-gray-500">Loading profile...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (status === "error" && !profile) {
        return (
            <div className="max-w-lg mx-auto">
                <div className="card p-6 text-center">
                    <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-6 h-6 text-danger-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <p className="text-danger-500 mb-4">{error}</p>
                    <button onClick={fetchProfile} className="btn-primary">
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-lg mx-auto">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Profile</h1>
                <p className="text-gray-500 mt-1">Manage your account information</p>
            </div>

            <div className="card p-6">
                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-sm text-danger-500">{error}</p>
                    </div>
                )}
                {success && (
                    <div className="mb-4 p-3 bg-accent-50 border border-accent-200 rounded-lg">
                        <p className="text-sm text-accent-600">{success}</p>
                    </div>
                )}

                <form onSubmit={handleSave} className="space-y-5">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Email
                        </label>
                        <input
                            type="email"
                            value={profile?.email || ""}
                            disabled
                            className="input bg-gray-100 text-gray-500 cursor-not-allowed"
                        />
                        <p className="text-xs text-gray-500 mt-1.5">Email cannot be changed</p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Name
                        </label>
                        <input
                            type="text"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            disabled={!isEditing}
                            placeholder="Enter your name"
                            className={`input ${!isEditing ? "bg-gray-100 text-gray-500 cursor-not-allowed" : ""}`}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Member Since
                        </label>
                        <input
                            type="text"
                            value={profile?.createdAt ? new Date(profile.createdAt).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric"
                            }) : ""}
                            disabled
                            className="input bg-gray-100 text-gray-500 cursor-not-allowed"
                        />
                    </div>

                    <div className="pt-2">
                        {isEditing ? (
                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    disabled={status === "saving"}
                                    className="btn-primary flex-1"
                                >
                                    {status === "saving" ? "Saving..." : "Save Changes"}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleCancel}
                                    disabled={status === "saving"}
                                    className="btn-secondary flex-1"
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => {
                                    setIsEditing(true);
                                    setSuccess("");
                                }}
                                className="btn-primary w-full"
                            >
                                Edit Profile
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}
